import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import {
  UserCircle, Plus, Trash, PencilSimple, SpinnerGap, Microphone, X, Check, Camera, UploadSimple, Waveform,
  ChatCenteredText, Crop,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { API_BASE as API } from "../lib/apiBase";
// Vincent B795 (Eric 2026-02-12) — Modale de recadrage photo.
import PhotoCropModal from "../components/speakers/PhotoCropModal";

// Vincent B696-enhancement complément (Eric 2026-02-10) — Format MM:SS
// pour le badge « Créé depuis Whisper ». Timestamp = seconds → deep link
// vers la vidéo au bon segment.
function formatMMSS(seconds) {
  if (seconds == null || isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Vincent B696-enhancement complément² (Eric 2026-02-10) — Formate le
// tooltip complet du badge « Créé depuis Whisper » : nom créateur +
// titre vidéo + timecode + date. Ex FR : « Créé par Éric depuis
// "01.Le Start up" à 02:07 le 10 févr. »
function formatOriginTooltip(speaker, lang) {
  const who = (speaker.created_by_name || "").trim();
  const title = (speaker.origin_video_title || "").trim();
  const ts = formatMMSS(speaker.origin_timestamp);
  let dateStr = "";
  if (speaker.created_at) {
    try {
      const d = new Date(speaker.created_at);
      dateStr = d.toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
        day: "numeric",
        month: "short",
        year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
      });
    } catch { /* fallback silencieux */ }
  }
  if (lang === "en") {
    return [
      who ? `Created by ${who}` : "Created",
      title ? `from "${title}"` : "from a Whisper transcript",
      `at ${ts}`,
      dateStr ? `on ${dateStr}` : "",
    ].filter(Boolean).join(" ") + ". Click to open the source video at that timecode.";
  }
  return [
    who ? `Créé par ${who}` : "Créé",
    title ? `depuis « ${title} »` : "depuis une transcription Whisper",
    `à ${ts}`,
    dateStr ? `le ${dateStr}` : "",
  ].filter(Boolean).join(" ") + ". Cliquez pour ouvrir la vidéo source à ce timecode.";
}

function VoiceprintBadge({ speakerId, lang }) {
  const [fp, setFp] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/speakers/${speakerId}/voiceprint`, { withCredentials: true })
      .then(r => setFp(r.data)).catch(() => {});
  }, [speakerId]);
  if (!fp || fp.status === "no_fingerprint") {
    return (
      <span className="text-[10px] px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-full font-medium">
        {lang === "en" ? "No voiceprint" : "Pas d'empreinte"}
      </span>
    );
  }
  const mins = Math.round(fp.total_speech_seconds / 60);
  return (
    <span className="text-[10px] px-2 py-0.5 bg-violet-500/10 text-violet-600 rounded-full font-medium flex items-center gap-1" data-testid={`voiceprint-${speakerId}`}>
      <Waveform size={10} />
      {lang === "en" ? `Voice ${mins}min / ${fp.video_count} vid` : `Voix ${mins}min / ${fp.video_count} vid`}
    </span>
  );
}

export default function SpeakersPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", description: "", can_be_named: true, user_id: "" });
  // Vincent B715 (Eric 2026-02-11) — Liste des utilisateurs de l'org pour
  // le dropdown "Associer à un utilisateur" (endpoint léger dédié aux
  // editeurs). Fetchée une seule fois au montage.
  const [orgUsers, setOrgUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  // Vincent B795 (Eric 2026-02-12) — État de la modale de recadrage.
  // `cropForSpeaker` = l'objet locuteur en cours de recadrage (ou null).
  const [cropForSpeaker, setCropForSpeaker] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [sortBy, setSortBy] = useState("first_asc");
  // Vincent B10 XLS 2026-08-10 (Eric 2026-02-10) — Rôles prédéfinis (cases
  // à cocher) : Formateur, Employé, Client, Gestionnaire, Fournisseur,
  // Coach. Multi-sélection = un locuteur peut cumuler plusieurs rôles
  // (ex. Employé + Coach). Champ libre conservé en complément.
  // Liste stricte imposée par Vincent (B711 — 2026-02-10). Ordre alphabétique,
  // masculin/féminin séparés pour cocher précisément. Multi-sélection permise.
  const PREDEFINED_ROLES = [
    { key: "adjoint",              fr: "Adjoint",              en: "Assistant (m)" },
    { key: "adjointe",             fr: "Adjointe",             en: "Assistant (f)" },
    { key: "admin_technique",      fr: "Admin technique",      en: "Tech Admin" },
    { key: "comptable",            fr: "Comptable",            en: "Accountant" },
    { key: "conseiller",           fr: "Conseiller",           en: "Advisor (m)" },
    { key: "conseillere",          fr: "Conseillère",          en: "Advisor (f)" },
    { key: "directeur",            fr: "Directeur",            en: "Director (m)" },
    { key: "directrice",           fr: "Directrice",           en: "Director (f)" },
    { key: "fondateur",            fr: "Fondateur",            en: "Founder (m)" },
    { key: "fondatrice",           fr: "Fondatrice",           en: "Founder (f)" },
    { key: "formateur",            fr: "Formateur",            en: "Trainer (m)" },
    { key: "formatrice",           fr: "Formatrice",           en: "Trainer (f)" },
    { key: "planificateur",        fr: "Planificateur",        en: "Planner (m)" },
    { key: "planificatrice",       fr: "Planificatrice",       en: "Planner (f)" },
    { key: "stagiaire",            fr: "Stagiaire",            en: "Intern" },
  ];
  // Filtre par rôle sur la liste (multi-sélection). Set vide = tous les
  // rôles visibles.
  const [roleFilter, setRoleFilter] = useState(new Set());
  const fileInputRef = useRef(null);

  // Tâche 1360 (Eric 2026-06-30) — Ce bouton écrasait des noms de locuteurs
  // saisis à la main. Il passe en DEUX temps : un premier clic simule et
  // chiffre, un second applique. Un nettoyage qui part au premier clic ne
  // laisse aucune chance de constater qu'il allait détruire du travail payé.
  const cleanupAllSpeakers = async (confirme = false) => {
    setCleaning(true);
    if (confirme) setCleanResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/cleanup-all-speakers`,
        { dry_run: !confirme },
        { withCredentials: true },
      );
      setCleanResult(data);
    } catch (e) { console.error(e); setCleanResult({ status: "error" }); }
    setCleaning(false);
  };

  

  useEffect(() => { load(); loadOrgUsers(); }, []);

  // Vincent B9 XLS 2026-08-10 (Eric 2026-02-10) — Tri en mémoire selon
  // le choix du dropdown. Décompose le nom en prénom (mot 1) et nom
  // (derniers mots) pour permettre les 4 modes prénom/nom asc/desc.
  const sortedSpeakers = (() => {
    // Vincent B10 XLS 2026-08-10 — Filtre par rôle appliqué AVANT le tri.
    // Un locuteur passe le filtre si SON `role` contient (case-insensitive)
    // n'importe lequel des labels FR ou EN cochés. Set vide = pas de
    // filtre (tous visibles).
    let arr = [...speakers];
    if (roleFilter.size > 0) {
      const _needles = PREDEFINED_ROLES.filter((r) => roleFilter.has(r.key))
        .flatMap((r) => [r.fr.toLowerCase(), r.en.toLowerCase()]);
      arr = arr.filter((s) => {
        const rL = (s.role || "").toLowerCase();
        return _needles.some((n) => rL.includes(n));
      });
    }
    const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { sensitivity: "base" });
    const firstName = (s) => (s.name || "").trim().split(/\s+/)[0] || "";
    const lastName = (s) => {
      const parts = (s.name || "").trim().split(/\s+/);
      return parts.length > 1 ? parts.slice(1).join(" ") : "";
    };
    switch (sortBy) {
      case "first_asc":  arr.sort((a, b) => cmp(firstName(a), firstName(b))); break;
      case "first_desc": arr.sort((a, b) => cmp(firstName(b), firstName(a))); break;
      case "last_asc":   arr.sort((a, b) => cmp(lastName(a) || firstName(a), lastName(b) || firstName(b))); break;
      case "last_desc":  arr.sort((a, b) => cmp(lastName(b) || firstName(b), lastName(a) || firstName(a))); break;
      case "role_asc":   arr.sort((a, b) => cmp(a.role, b.role) || cmp(firstName(a), firstName(b))); break;
      case "role_desc":  arr.sort((a, b) => cmp(b.role, a.role) || cmp(firstName(a), firstName(b))); break;
      default: break;
    }
    return arr;
  })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/speakers?include_photos=true`, { withCredentials: true });
      setSpeakers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Vincent B715 — Charge la liste des utilisateurs pour le dropdown
  // d'association. Silencieux en cas d'erreur (dropdown reste vide).
  const loadOrgUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/speakers/org-users`, { withCredentials: true });
      setOrgUsers(Array.isArray(data) ? data : []);
    } catch (_) { /* silencieux */ }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/api/speakers/${editId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/speakers`, form, { withCredentials: true });
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: "", role: "", description: "", can_be_named: true, user_id: "" });
      load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirmDialog(lang === "en" ? "Delete this speaker?" : "Supprimer ce locuteur?");
    if (!ok) return;
    await axios.delete(`${API}/api/speakers/${id}`, { withCredentials: true });
    load();
  };

  const handlePhotoUpload = async (speakerId, file) => {
    if (!file) return;
    setUploadingPhoto(speakerId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/api/speakers/${speakerId}/photo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      // Vincent B795 (Eric 2026-02-12) — Ouvre AUTOMATIQUEMENT la modale
      // de recadrage après l'upload d'une nouvelle photo, pour que
      // l'utilisateur puisse recentrer immédiatement (sans devoir
      // recliquer). Le crop backend a été réinitialisé au centre.
      const { data: refreshed } = await axios.get(
        `${API}/api/speakers?include_photos=false`,
        { withCredentials: true }
      );
      setSpeakers(refreshed);
      const updated = refreshed.find((s) => s.id === speakerId);
      if (updated) {
        setCropForSpeaker(updated);
      }
    } catch (e) {
      console.error(e);
      toast.error(lang === "fr" ? "Échec du téléversement" : "Upload failed");
    }
    setUploadingPhoto(null);
  };

  // Vincent B795 (Eric 2026-02-12) — Sauvegarde les paramètres de crop
  // via PATCH puis rafraîchit la liste pour que les avatars affichent la
  // nouvelle version (cache-bust via `photo_updated_at` bumped côté API).
  const handleCropSave = async (crop) => {
    if (!cropForSpeaker) return;
    try {
      await axios.patch(
        `${API}/api/speakers/${cropForSpeaker.id}/photo-crop`,
        crop,
        { withCredentials: true }
      );
      toast.success(lang === "fr" ? "Recadrage enregistré" : "Crop saved");
      setCropForSpeaker(null);
      load();
    } catch (e) {
      console.error("[B795] crop save failed", e);
      toast.error(
        (lang === "fr" ? "Échec : " : "Failed: ") +
          (e?.response?.data?.detail || e.message)
      );
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({
      name: s.name,
      role: s.role || "",
      description: s.description || "",
      can_be_named: s.can_be_named !== false,
      // Vincent B715 — Pré-remplit l'association user_id existante (si le
      // user existe toujours dans l'org, backend fournit `associated_user`).
      user_id: s.user_id || "",
    });
    setShowAdd(true);
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  // Vincent B717 (Eric 2026-02-11) — Le formulaire s'affiche INLINE dans
  // la carte du locuteur qu'on édite (au lieu de tout en haut). Vincent :
  // « ça doit être une fiche qui apparait sous le crayon qu'on vient de
  // cliquer pour éditer, et non des champs tout en haut de la page ».
  // Extrait en fonction pour éviter la duplication (~150 lignes de JSX).
  // Ordre d'appel :
  //  - AJOUT (new)  → rendu tout en haut (position historique)
  //  - ÉDITION      → rendu INLINE à la place de la carte éditée
  const renderSpeakerForm = () => (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4 animate-fade-in" data-testid="speaker-form">
      <h3 className="font-semibold text-sm">{editId ? (lang === "en" ? "Edit speaker" : "Modifier") : (lang === "en" ? "New speaker" : "Nouveau locuteur")}</h3>
      {/* Vincent B724 (Eric 2026-02-12) — Nom SEUL sur sa ligne pour être
          entièrement lisible ; Role et Description sur la ligne suivante
          (côte à côte ≥md, empilés en mobile). */}
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] font-semibold text-zinc-600 mb-1">
            {lang === "en" ? "Full name *" : "Nom complet *"}
          </label>
          <Input
            placeholder={lang === "en" ? "e.g. Jean-Sébastien Hétu" : "ex. Jean-Sébastien Hétu"}
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            data-testid="speaker-name-input"
            className="w-full text-base"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">
              {lang === "en" ? "Role" : "Rôle"}
            </label>
            <Input placeholder={lang === "en" ? "e.g. CEO, Advisor" : "ex. CEO, Conseiller"} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="speaker-role-input" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">
              {lang === "en" ? "Description (optional)" : "Description (optionnel)"}
            </label>
            <Input placeholder={lang === "en" ? "Description" : "Description"} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="speaker-desc-input" />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="predefined-roles">
        <span className="text-muted-foreground font-medium">
          {lang === "en" ? "Predefined roles" : "Rôles prédéfinis"} :
        </span>
        {PREDEFINED_ROLES.map((r) => {
          const label = lang === "en" ? r.en : r.fr;
          const roleValue = form.role || "";
          const tokens = roleValue.split(/\s*\/\s*/).filter(Boolean);
          const checked = tokens.some(
            (t) => t.toLowerCase() === r.fr.toLowerCase() || t.toLowerCase() === r.en.toLowerCase(),
          );
          return (
            <label
              key={r.key}
              className="flex items-center gap-1.5 px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 cursor-pointer"
              data-testid={`predefined-role-${r.key}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  setForm((f) => {
                    const cur = (f.role || "").split(/\s*\/\s*/).filter(Boolean);
                    const has = cur.some(
                      (t) => t.toLowerCase() === r.fr.toLowerCase() || t.toLowerCase() === r.en.toLowerCase(),
                    );
                    const next = has
                      ? cur.filter(
                          (t) =>
                            t.toLowerCase() !== r.fr.toLowerCase() &&
                            t.toLowerCase() !== r.en.toLowerCase(),
                        )
                      : [...cur, label];
                    return { ...f, role: next.join(" / ") };
                  });
                }}
                className="w-3.5 h-3.5 rounded border-zinc-300"
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
      <label className="flex items-center gap-2 cursor-pointer" data-testid="can-be-named-toggle">
        <input
          type="checkbox"
          checked={form.can_be_named}
          onChange={e => setForm(f => ({ ...f, can_be_named: e.target.checked }))}
          className="w-4 h-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]"
        />
        <span className="text-sm">
          {lang === "en" ? "Can be named in chatbot responses" : "Peut être nommé dans les réponses du chatbot"}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {form.can_be_named
            ? (lang === "en" ? "(Formateur — name visible)" : "(Formateur — nom visible)")
            : (lang === "en" ? "(Participant — anonymous)" : "(Participant — anonyme)")}
        </span>
      </label>
      {(() => {
        const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
        const speakerName = norm(form.name);
        const suggestedUser = !form.user_id && speakerName
          ? orgUsers.find((u) => norm(u.name) === speakerName)
          : null;
        return (
          <div className="space-y-1.5" data-testid="speaker-user-assoc-group">
            <label className="text-xs font-semibold text-zinc-700">
              {lang === "en" ? "Linked platform user" : "Utilisateur plateforme associé"}
              <span className="ml-1 text-zinc-400 font-normal">
                {lang === "en" ? "(optional)" : "(optionnel)"}
              </span>
            </label>
            {suggestedUser && (
              <div
                className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-violet-200 bg-violet-50 text-[11px]"
                data-testid="speaker-user-suggestion"
              >
                <span aria-hidden="true">💡</span>
                <span className="flex-1 text-violet-900">
                  {lang === "en" ? "Same-name user found: " : "Utilisateur au même nom trouvé : "}
                  <strong>{suggestedUser.name}</strong>
                  {suggestedUser.email && (
                    <span className="text-violet-700 ml-1">({suggestedUser.email})</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, user_id: suggestedUser.id }))}
                  className="px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold shadow-sm"
                  data-testid="speaker-user-suggestion-apply-btn"
                >
                  {lang === "en" ? "Link in 1 click" : "Associer en 1 clic"}
                </button>
              </div>
            )}
            <select
              value={form.user_id || ""}
              onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
              data-testid="speaker-user-select"
              className="w-full h-9 px-3 rounded border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            >
              <option value="">
                {lang === "en" ? "— No linked user —" : "— Aucun utilisateur associé —"}
              </option>
              {orgUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.email ? ` — ${u.email}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-500">
              {lang === "en"
                ? "Linking lets other users message this speaker directly from the video Speakers tab."
                : "L'association permet aux autres utilisateurs de démarrer une conversation avec ce locuteur depuis l'onglet Locuteurs d'une vidéo."}
            </p>
          </div>
        );
      })()}
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving || !form.name.trim()} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-speaker-btn">
          {saving ? <SpinnerGap size={14} className="animate-spin" /> : <Check size={14} />}
          {lang === "en" ? "Save" : "Enregistrer"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setShowAdd(false); setEditId(null); }} data-testid="cancel-speaker-btn">
          <X size={14} />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="speakers-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-violet-500/20 flex items-center justify-center">
            <Microphone size={24} className="text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="speakers-title">
              {lang === "en" ? "Speaker Profiles" : "Profils des locuteurs"}
            </h1>
            <p className="text-xs text-muted-foreground">
              {lang === "en"
                ? "Add photos and info for automatic visual identification in videos"
                : "Ajoutez les photos et infos pour l'identification visuelle automatique dans les vidéos"}
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => { setShowAdd(true); setEditId(null); setForm({ name: "", role: "", description: "", can_be_named: true, user_id: "" }); }} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="add-speaker-btn">
          <Plus size={14} /> {lang === "en" ? "Add speaker" : "Ajouter"}
        </Button>
      </div>

      {/* Cleanup fictitious speakers */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3" data-testid="cleanup-section">
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {lang === "en" ? "Fictitious speaker cleanup" : "Nettoyage des locuteurs fictifs"}
          </p>
          <p className="text-xs text-amber-700">
            {lang === "en"
              ? "Remove AI-invented speaker names and reassign those segments to a real profile. Segments renamed by hand (speaker merge) are never touched."
              : "Supprimer les noms de locuteurs inventés par l'IA et rattacher ces segments à un vrai profil. Les segments renommés à la main (fusion de locuteurs) ne sont jamais touchés."}
          </p>
          {cleanResult && cleanResult.status === "simulation" && (
            <p className="text-xs text-amber-900 mt-1 font-medium" data-testid="cleanup-simulation-result">
              {lang === "en"
                ? `Simulation: ${cleanResult.total_segments_fixed} segment(s) would change. Names found: ${cleanResult.fictitious_names?.join(", ")}`
                : `Simulation : ${cleanResult.total_segments_fixed} segment(s) seraient modifiés. Noms repérés : ${cleanResult.fictitious_names?.join(", ")}`}
            </p>
          )}
          {cleanResult && cleanResult.status === "cleaned" && (
            <p className="text-xs text-teal-700 mt-1 font-medium">
              {lang === "en"
                ? `Done! ${cleanResult.total_segments_fixed} segments fixed. Fictitious names removed: ${cleanResult.fictitious_names?.join(", ")}`
                : `Terminé ! ${cleanResult.total_segments_fixed} segments corrigés. Noms fictifs supprimés : ${cleanResult.fictitious_names?.join(", ")}`}
            </p>
          )}
          {cleanResult && cleanResult.status === "clean" && (
            <p className="text-xs text-teal-700 mt-1 font-medium">
              {lang === "en" ? "All clean! No fictitious speakers found." : "Tout est propre ! Aucun locuteur fictif trouvé."}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {cleanResult && cleanResult.status === "simulation" && cleanResult.total_segments_fixed > 0 && (
            <Button
              size="sm"
              onClick={() => cleanupAllSpeakers(true)}
              disabled={cleaning}
              className="gap-1.5 bg-amber-700 text-white hover:bg-amber-800"
              data-testid="cleanup-speakers-confirm-btn"
            >
              {lang === "en"
                ? `Confirm: ${cleanResult.total_segments_fixed} segment(s)`
                : `Confirmer : ${cleanResult.total_segments_fixed} segment(s)`}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => cleanupAllSpeakers(false)}
            disabled={cleaning}
            className="gap-1.5 border-amber-400 text-amber-800 hover:bg-amber-100"
            data-testid="cleanup-speakers-btn"
          >
            {cleaning ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} />}
            {lang === "en" ? "Simulate cleanup" : "Simuler le nettoyage"}
          </Button>
        </div>
      </div>

      {/* Vincent B10 XLS 2026-08-10 (Eric 2026-02-10) — Filtre par rôle
          (multi-sélection) au-dessus de la liste. Un locuteur avec 
          `role="Formateur / Coach"` sera inclus si l'un des deux est
          coché. Vide = pas de filtre. */}
      {speakers.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="role-filter">
          <span className="font-medium text-muted-foreground">
            {lang === "en" ? "Filter by role" : "Filtrer par rôle"} :
          </span>
          {PREDEFINED_ROLES.map((r) => {
            const active = roleFilter.has(r.key);
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => {
                  setRoleFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(r.key)) next.delete(r.key); else next.add(r.key);
                    return next;
                  });
                }}
                className={`px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                }`}
                data-testid={`role-filter-${r.key}`}
                aria-pressed={active}
              >
                {lang === "en" ? r.en : r.fr}
              </button>
            );
          })}
          {roleFilter.size > 0 && (
            <button
              type="button"
              onClick={() => setRoleFilter(new Set())}
              className="text-zinc-500 hover:text-zinc-800 underline text-[11px] ml-1"
              data-testid="role-filter-clear"
            >
              {lang === "en" ? "Clear" : "Effacer"}
            </button>
          )}
        </div>
      )}

      {/* Vincent B9 XLS 2026-08-10 (Eric 2026-02-10) — Menu de tri : 6 modes
          prénom/nom/rôle × asc/desc. Placé à droite du bloc « Nettoyer les
          locuteurs fictifs » pour un accès rapide sans scroll. */}
      {speakers.length > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <label className="font-medium" htmlFor="speakers-sort-select">
            {lang === "en" ? "Sort by" : "Trier par"} :
          </label>
          <select
            id="speakers-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs border border-zinc-300 rounded px-2 py-1 bg-white focus:border-[#002FA7] outline-none"
            data-testid="speakers-sort-select"
          >
            <option value="first_asc">{lang === "en" ? "First name A→Z" : "Prénom A→Z"}</option>
            <option value="first_desc">{lang === "en" ? "First name Z→A" : "Prénom Z→A"}</option>
            <option value="last_asc">{lang === "en" ? "Last name A→Z" : "Nom A→Z"}</option>
            <option value="last_desc">{lang === "en" ? "Last name Z→A" : "Nom Z→A"}</option>
            <option value="role_asc">{lang === "en" ? "Role A→Z" : "Rôle A→Z"}</option>
            <option value="role_desc">{lang === "en" ? "Role Z→A" : "Rôle Z→A"}</option>
          </select>
        </div>
      )}

      {/* Add/Edit Form
          Vincent B717 (Eric 2026-02-11) — Le formulaire d'ÉDITION s'affiche
          désormais INLINE (dans la carte du locuteur qu'on édite) au lieu
          d'apparaître tout en haut de la page. Le formulaire d'AJOUT (new)
          reste au sommet pour rester visible immédiatement. On rend le
          form ici quand `showAdd && !editId`, et on remplace la carte
          plus bas quand `editId === s.id`. Pour éviter de dupliquer
          ~150 lignes de JSX, on définit une fonction locale
          `renderSpeakerForm()` juste avant le point d'insertion inline. */}
      {showAdd && !editId && renderSpeakerForm()}

      {/* Speaker Cards */}
      {speakers.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground bg-card border border-border rounded-xl">
          <Microphone size={48} className="mx-auto mb-4 opacity-20" />
          <p className="font-medium">{lang === "en" ? "No speakers defined yet" : "Aucun locuteur defini"}</p>
          <p className="text-sm mt-1">{lang === "en" ? "Add speaker profiles with photos for automatic visual identification" : "Ajoutez des profils avec photos pour l'identification visuelle automatique"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="speakers-list">
          {sortedSpeakers.map(s => (
            editId === s.id ? (
              // Vincent B717 — Le form remplace la carte en édition.
              // Vincent B724 (Eric 2026-02-12) — En édition, le formulaire
              // s'étend sur TOUTE la largeur du bandeau (col-span) pour que
              // le champ Nom soit entièrement lisible même en 3 colonnes.
              <div key={s.id} className="md:col-span-2 xl:col-span-3">
                {renderSpeakerForm()}
              </div>
            ) : (
            <div key={s.id} className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors" data-testid={`speaker-${s.id}`}>
              <div className="flex items-start gap-4">
                {/* Photo — Eric (2026-05-12) fix : utilise has_photo retourné par
                    /api/speakers (le backend scrubbe photo_base64 et photo_mime
                    de la réponse listing pour éviter les payloads énormes). */}
                <div className="relative group flex-shrink-0">
                  {s.has_photo || s.photo_base64 || s.photo_mime ? (
                    <img
                      src={`${API}/api/speakers/${s.id}/photo?cropped=1&t=${s.photo_updated_at || ""}`}
                      alt={s.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-violet-500/30"
                      data-testid={`speaker-photo-${s.id}`}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-blue-500/20 flex items-center justify-center border-2 border-dashed border-violet-500/30">
                      <span className="text-2xl font-bold text-violet-400">{s.name.charAt(0)}</span>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      fileInputRef.current?.setAttribute("data-speaker-id", s.id);
                      fileInputRef.current?.click();
                    }}
                    className="absolute -bottom-1 -right-1 h-7 px-2 bg-primary hover:bg-primary/90 text-white rounded-full flex items-center justify-center gap-1 shadow-lg text-[10px] font-semibold"
                    data-testid={`upload-photo-${s.id}`}
                    title={lang === "en" ? "Upload a photo for this speaker (JPG, PNG, WebP)" : "Téléverser une photo pour ce locuteur (JPG, PNG, WebP)"}
                  >
                    {uploadingPhoto === s.id ? (
                      <><SpinnerGap size={12} className="animate-spin" /> {lang === "en" ? "..." : "..."}</>
                    ) : (
                      <><Camera size={12} /> <span>{(s.has_photo || s.photo_base64 || s.photo_mime) ? (lang === "en" ? "Change" : "Changer") : (lang === "en" ? "Add photo" : "Ajouter")}</span></>
                    )}
                  </button>
                  {/* Vincent B795 (Eric 2026-02-12) — Bouton « Recadrer »
                      visible uniquement si une photo existe. Ouvre la
                      modale de crop sans réupload. */}
                  {(s.has_photo || s.photo_base64 || s.photo_mime) && (
                    <button
                      onClick={() => setCropForSpeaker(s)}
                      className="absolute -top-1 -right-1 h-6 w-6 bg-white hover:bg-zinc-100 text-blue-600 border border-blue-300 rounded-full flex items-center justify-center shadow"
                      data-testid={`recrop-photo-${s.id}`}
                      title={lang === "en" ? "Recenter / recrop the photo" : "Recentrer / recadrer la photo"}
                      aria-label={lang === "en" ? "Recrop photo" : "Recadrer la photo"}
                      type="button"
                    >
                      <Crop size={12} weight="bold" />
                    </button>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm break-words" data-testid={`speaker-name-${s.id}`}>{s.name}</p>
                  {s.role && <p className="text-xs text-primary/80 break-words">{s.role}</p>}
                  {s.description && <p className="text-xs text-muted-foreground mt-0.5 break-words">{s.description}</p>}
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {(s.has_photo || s.photo_base64 || s.photo_mime) ? (
                      <span className="text-[10px] px-2 py-0.5 bg-teal-500/10 text-teal-500 rounded-full font-medium">
                        {lang === "en" ? "Photo OK" : "Photo OK"}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-500 rounded-full font-medium">
                        {lang === "en" ? "No photo" : "Pas de photo"}
                      </span>
                    )}
                    {s.can_be_named === false ? (
                      <span className="text-[10px] px-2 py-0.5 bg-red-500/10 text-red-500 rounded-full font-medium" data-testid={`speaker-anonymous-${s.id}`}>
                        {lang === "en" ? "Anonymous in chat" : "Anonyme dans le chat"}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded-full font-medium" data-testid={`speaker-named-${s.id}`}>
                        {lang === "en" ? "Named in chat" : "Nommé dans le chat"}
                      </span>
                    )}
                    <VoiceprintBadge speakerId={s.id} lang={lang} />
                    {/* Vincent B715 (Eric 2026-02-11) — Badge "utilisateur
                        associé". Nom + email en tooltip. Visible seulement
                        si le user existe TOUJOURS dans l'org (backend
                        nettoie les refs orphelines). */}
                    {s.associated_user && (
                      <span
                        className="text-[10px] px-2 py-0.5 bg-violet-500/10 text-violet-600 rounded-full font-medium inline-flex items-center gap-1"
                        data-testid={`speaker-linked-user-${s.id}`}
                        title={s.associated_user.email || ""}
                      >
                        <span aria-hidden="true">👤</span>
                        {s.associated_user.name}
                      </span>
                    )}
                    {/* Vincent B696-enhancement complément³ (Eric 2026-02-10)
                        — Compteur d'utilisation : nombre de vidéos +
                        nombre de phrases attribuées à ce locuteur.
                        Aide à repérer les gros contributeurs vs les
                        locuteurs orphelins (0/0 = candidat suppression). */}
                    {typeof s.usage_video_count === "number" && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                          s.usage_video_count === 0
                            ? "bg-zinc-200 text-zinc-500"
                            : s.usage_video_count >= 5
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-sky-500/10 text-sky-600"
                        }`}
                        title={
                          lang === "en"
                            ? `Used in ${s.usage_video_count} video${s.usage_video_count !== 1 ? "s" : ""}, ${s.usage_segment_count || 0} sentence${(s.usage_segment_count || 0) !== 1 ? "s" : ""}${s.usage_video_count === 0 ? " — this speaker is not used in any transcript yet." : ""}`
                            : `Utilisé dans ${s.usage_video_count} vidéo${s.usage_video_count !== 1 ? "s" : ""}, ${s.usage_segment_count || 0} phrase${(s.usage_segment_count || 0) !== 1 ? "s" : ""}${s.usage_video_count === 0 ? " — ce locuteur n'est utilisé dans aucune transcription pour l'instant." : ""}`
                        }
                        data-testid={`speaker-usage-${s.id}`}
                      >
                        <span className="tabular-nums">{s.usage_video_count}</span>
                        <span className="opacity-70 ml-0.5">{lang === "en" ? "vid" : "vid."}</span>
                        <span className="opacity-40 mx-0.5">·</span>
                        <span className="tabular-nums">{s.usage_segment_count || 0}</span>
                        <span className="opacity-70 ml-0.5">{lang === "en" ? "sent." : "phr."}</span>
                      </span>
                    )}
                    {/* Vincent B696-enhancement complément (Eric 2026-02-10)
                        — Badge « Créé depuis Whisper » avec lien direct
                        vers la vidéo d'origine au timestamp du segment
                        d'où le locuteur a été créé. Permet de retracer le
                        contexte d'un locuteur mystérieux (ex. « Client 2s »)
                        sans deviner. */}
                    {s.origin_source === "whisper_transcript" && s.origin_video_id && (
                      <Link
                        to={`/dashboard/videos/${s.origin_video_id}?t=${Math.floor(s.origin_timestamp || 0)}&tab=transcript&source=whisper`}
                        className="text-[10px] px-2 py-0.5 bg-violet-500/10 text-violet-600 hover:bg-violet-500/20 rounded-full font-medium flex items-center gap-1 transition-colors"
                        title={formatOriginTooltip(s, lang)}
                        data-testid={`speaker-origin-whisper-${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChatCenteredText size={10} weight="fill" />
                        {lang === "en" ? "From Whisper" : "Créé depuis Whisper"}
                        <span className="opacity-70">·</span>
                        <span className="tabular-nums">{formatMMSS(s.origin_timestamp)}</span>
                      </Link>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-secondary rounded-lg transition-colors" data-testid={`edit-speaker-${s.id}`}>
                    <PencilSimple size={16} className="text-muted-foreground" />
                  </button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`delete-speaker-${s.id}`}>
                    <Trash size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
            )
          ))}
        </div>
      )}

      {/* Hidden file input for photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const speakerId = e.target.getAttribute("data-speaker-id");
          if (file && speakerId) handlePhotoUpload(speakerId, file);
          e.target.value = "";
        }}
      />

      {/* Vincent B795 (Eric 2026-02-12) — Modale de recadrage.
          - Image source : URL brute (sans ?cropped=1) pour laisser
            l'utilisateur ajuster à partir du fichier original.
          - Sauvegarde : PATCH /api/speakers/{id}/photo-crop. */}
      {cropForSpeaker && (
        <PhotoCropModal
          sourceUrl={`${API}/api/speakers/${cropForSpeaker.id}/photo?t=${cropForSpeaker.photo_updated_at || ""}`}
          initialCrop={cropForSpeaker.photo_crop || null}
          speakerName={cropForSpeaker.name}
          lang={lang}
          onSave={handleCropSave}
          onCancel={() => setCropForSpeaker(null)}
        />
      )}

      {/* How it works */}
      <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-5 text-sm space-y-3">
        <p className="font-semibold text-violet-400">{lang === "en" ? "How visual speaker identification works" : "Comment fonctionne l'identification visuelle"}</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-muted-foreground">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
            <span>{lang === "en" ? "Upload a photo for each known speaker" : "Uploadez une photo pour chaque locuteur connu"}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
            <span>{lang === "en" ? "Upload a video — the AI extracts frames automatically" : "Téléversez une vidéo — l'IA extrait les images automatiquement"}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
            <span>{lang === "en" ? "GPT-5.2 Vision compares faces in frames to your photos" : "GPT-5.2 Vision compare les visages dans les images à vos photos"}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
            <span>{lang === "en" ? "Validate, then auto-generate quizzes, Q&A, scripts & procedures" : "Validez, puis génération automatique des quiz, Q&R, scripts et procédures"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
